import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { ApiService } from './../../modules/shared/_services/api.service';
import { environment } from 'src/environments/environment';
import { ENDPOINTS } from 'src/app/_configs/endpoints';

// Define state keys for each API endpoint
const STATES_LIST_KEY = makeStateKey<any[]>('statesList');
const CATEGORIES_LIST_KEY = makeStateKey<any[]>('categoriesList');
const KEYWORDS_LIST_KEY = makeStateKey<any[]>('keywordsList');
const AUTHORITIES_LIST_KEY = makeStateKey<any[]>('authoritiesList');
const PROCUREMENT_LIST_KEY = makeStateKey<any[]>('procurementList');
const ADDED_IN_LAST_THREE_KEY = makeStateKey<any[]>('addedInLastThreeDays');
const TRENDING_SEARCH = makeStateKey<any[]>('trendingSearch');

@Injectable({
  providedIn: 'root'
})
export class TendersResolver implements Resolve<any> {

  constructor(
    private apiService: ApiService,
    private transferState: TransferState
  ) { }

  resolve(): Observable<any> {
    // Check if TransferState has cached data
    if (
      this.transferState.hasKey(STATES_LIST_KEY) &&
      this.transferState.hasKey(CATEGORIES_LIST_KEY) &&
      this.transferState.hasKey(KEYWORDS_LIST_KEY) &&
      this.transferState.hasKey(AUTHORITIES_LIST_KEY) &&
      this.transferState.hasKey(PROCUREMENT_LIST_KEY) &&
      this.transferState.hasKey(ADDED_IN_LAST_THREE_KEY) &&
      this.transferState.hasKey(TRENDING_SEARCH)
    ) {
      // Retrieve and remove cached data from TransferState
      const states = this.transferState.get(STATES_LIST_KEY, []);
      const categories = this.transferState.get(CATEGORIES_LIST_KEY, []);
      const keywords = this.transferState.get(KEYWORDS_LIST_KEY, []);
      const authorities = this.transferState.get(AUTHORITIES_LIST_KEY, []);
      const procurement = this.transferState.get(PROCUREMENT_LIST_KEY, []);
      const newlyadded = this.transferState.get(ADDED_IN_LAST_THREE_KEY, []);
      const trendingSearch = this.transferState.get(TRENDING_SEARCH, []);

      // Clean up TransferState
      this.transferState.remove(STATES_LIST_KEY);
      this.transferState.remove(CATEGORIES_LIST_KEY);
      this.transferState.remove(KEYWORDS_LIST_KEY);
      this.transferState.remove(AUTHORITIES_LIST_KEY);
      this.transferState.remove(PROCUREMENT_LIST_KEY);
      this.transferState.remove(ADDED_IN_LAST_THREE_KEY);
      this.transferState.remove(TRENDING_SEARCH);
      return of({ states, categories, keywords, authorities, procurement, newlyadded, trendingSearch });
    } else {
      // Define URLs for API calls
      const urls = {
        states: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_STATE}`,
        categories: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_CATEGORY}`,
        keywords: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_KEYWORD}`,
        authorities: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_AUTHORITY}`,
        procurement: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.TENDER_SUMMARY_BY_PROCUREMENT}`,
        newlyadded: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.NEWLY_ADDED_TENDERS}`,
        trendingSearch: `${environment.TENDER.API_BASE_URL}${ENDPOINTS.TENDER.CMS.TRENDING_SEARCH}`
      };

      // Create observables with timeout and error handling
      const apiCalls$ = Object.keys(urls).map(key =>
        this.apiService.get(urls[key as keyof typeof urls]).pipe(
          timeout(5000), // Increased timeout
          catchError(error => {
            console.error(`Error fetching ${key}:`, error);
            return of({ data: [] });
          })
        )
      );

      return forkJoin({
        states: apiCalls$[0],
        categories: apiCalls$[1],
        keywords: apiCalls$[2],
        authorities: apiCalls$[3],
        procurement: apiCalls$[4],
        newlyadded: apiCalls$[5],
        trendingSearch: apiCalls$[6]
      }).pipe(
        tap(data => {
          // Cache data to TransferState for SSR
          this.transferState.set(STATES_LIST_KEY, data.states);
          this.transferState.set(CATEGORIES_LIST_KEY, data.categories);
          this.transferState.set(KEYWORDS_LIST_KEY, data.keywords);
          this.transferState.set(AUTHORITIES_LIST_KEY, data.authorities);
          this.transferState.set(PROCUREMENT_LIST_KEY, data.procurement);
          this.transferState.set(ADDED_IN_LAST_THREE_KEY, data.newlyadded);
          this.transferState.set(TRENDING_SEARCH, data.trendingSearch);
        })
      );
    }
  }
}